import { useState, useEffect } from 'react';
import { Lock, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { useTags } from '@/hooks/use-tags';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/errors';
import type { EmployeeNote } from '@/types/employee-note';

interface EmployeeNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  note?: EmployeeNote | null;
  onSave: (data: { title?: string; content: string; isPrivate: boolean; tagIds?: string[] }) => Promise<void>;
  isSaving: boolean;
}

export function EmployeeNoteDialog({
  open,
  onOpenChange,
  note,
  onSave,
  isSaving,
}: EmployeeNoteDialogProps) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const { data: tags = [], isLoading: tagsLoading } = useTags();
  const isEditMode = !!note;

  useEffect(() => {
    if (open) {
      if (note) {
        setTitle(note.title || '');
        setContent(note.content);
        setIsPrivate(note.isPrivate);
        setSelectedTagIds(note.tags?.map((t) => t.id) || []);
      } else {
        setTitle('');
        setContent('');
        setIsPrivate(false);
        setSelectedTagIds([]);
      }
      setError(null);
    }
  }, [open, note]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const textContent = content.replace(/<[^>]*>/g, '').trim();
    if (!textContent) {
      setError('Note content is required');
      return;
    }

    try {
      await onSave({
        title: title.trim() || undefined,
        content,
        isPrivate,
        tagIds: selectedTagIds.length > 0 ? selectedTagIds : undefined,
      });
      onOpenChange(false);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to save note'));
    }
  };

  const toggleTag = (tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="text-lg">
            {isEditMode ? 'Edit Note' : 'New Note'}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Document important information about this employee
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          <div className="flex-1 overflow-y-auto px-6 space-y-5">
            {/* Title — inline input, no label */}
            <input
              placeholder="Note title (optional)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={255}
              disabled={isSaving}
              className="w-full text-base font-medium bg-transparent border-0 border-b border-border/50 pb-2 placeholder:text-muted-foreground/50 focus:border-hygge focus:outline-none transition-colors disabled:opacity-50"
            />

            {/* Content */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Content <span className="text-destructive">*</span>
              </Label>
              <RichTextEditor
                content={content}
                onChange={setContent}
                placeholder="Write your note here..."
                editable={!isSaving}
              />
            </div>

            {/* Tags */}
            {!tagsLoading && tags.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Tags
                </Label>
                <div className="flex flex-wrap gap-1.5">
                  {tags.map((tag) => {
                    const isSelected = selectedTagIds.includes(tag.id);
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => toggleTag(tag.id)}
                        disabled={isSaving}
                        className={cn(
                          'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
                          isSelected ? 'text-white shadow-sm' : 'bg-muted/50 hover:bg-muted',
                        )}
                        style={{
                          ...(isSelected
                            ? { backgroundColor: tag.color }
                            : { color: tag.color, borderColor: tag.color }),
                        }}
                      >
                        {tag.name}
                        {isSelected && <X className="h-2.5 w-2.5" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 mt-4 border-t border-border/50">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <Checkbox
                id="private"
                checked={isPrivate}
                onCheckedChange={(checked) => setIsPrivate(checked === true)}
                disabled={isSaving}
              />
              <Lock className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Private</span>
            </label>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onOpenChange(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={isSaving} className="gap-1.5">
                <Save className="h-3.5 w-3.5" />
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
